// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushUtils.js
import { clampOpacity, clampSoftness } from './toolState';
import { markCanvasDirty } from './canvasUtils';
import { clipContextToSelection } from './selectionUtils';
import {
  combineSeed,
  buildJitterBlobProfile,
  getJitterBrushSpacing,
  getJitterBlobScale,
} from './brushJitter';

export function getBrushSoftMetrics(size, softness) {
  const radius = Math.max(0.5, Number(size || 0) / 2);
  const safeSoftness = clampSoftness(softness);
  const softnessRatio = safeSoftness / 100;
  const blurRadius = radius * softnessRatio * 0.9;
  const coreRadius = Math.max(0.5, radius - blurRadius);
  return {
    radius,
    blurRadius,
    coreRadius,
    softness: safeSoftness,
  };
}

function getStrokeSeed(points, options) {
  if (Number.isFinite(options && options.strokeSeed)) return options.strokeSeed;
  const first = points && points[0] ? points[0] : { x: 0, y: 0 };
  const last = points && points.length ? points[points.length - 1] : first;
  return combineSeed(
    first.x * 100,
    first.y * 100,
    last.x * 100,
    last.y * 100,
    Number(options && options.size) || 0,
    Number(options && options.softness) || 0,
    Number(options && options.jitterAmount) || 0,
    points ? points.length : 0,
  );
}

function drawWatercolorBlob(ctx, point, options, strokeSeed, dabIndex) {
  const safeOpacity = clampOpacity(options.opacity);
  const fillStyle = options.isEraser ? 'rgba(0,0,0,1)' : options.rgba;
  const { radius, blurRadius, softness } = getBrushSoftMetrics(options.size, options.softness);
  const settings = options.brushSettings || {};
  const baseSeed = combineSeed(strokeSeed, dabIndex, point.x * 100, point.y * 100);
  const layerCount = 3;
  const blobScale = getJitterBlobScale(settings);

  ctx.save();
  ctx.globalCompositeOperation = options.isEraser ? 'destination-out' : 'source-over';
  ctx.fillStyle = fillStyle;
  ctx.strokeStyle = fillStyle;
  ctx.shadowBlur = 0;
  ctx.filter = blurRadius > 0.01 ? `blur(${Math.max(0.35, blurRadius * 0.55)}px)` : 'none';

  for (let layerIndex = 0; layerIndex < layerCount; layerIndex += 1) {
    const profile = buildJitterBlobProfile(
      combineSeed(baseSeed, layerIndex * 911),
      dabIndex + layerIndex,
      radius,
      settings,
    );
    const layerRadius = radius * (0.82 + layerIndex * 0.06) * blobScale;
    const alphaScale = layerIndex === 0 ? 0.58 : layerIndex === 1 ? 0.34 : 0.22;
    const centerX = point.x + profile.offsetX;
    const centerY = point.y + profile.offsetY;

    ctx.globalAlpha = safeOpacity * alphaScale;
    ctx.beginPath();
    ctx.ellipse(
      centerX,
      centerY,
      Math.max(0.5, layerRadius * profile.stretchX),
      Math.max(0.5, layerRadius * profile.stretchY),
      profile.rotation,
      0,
      Math.PI * 2,
    );
    ctx.closePath();
    ctx.fill();

    if (!options.isEraser && softness > 0) {
      const bloomRadius = layerRadius * 0.94;
      ctx.globalAlpha = safeOpacity * alphaScale * 0.12;
      ctx.beginPath();
      ctx.ellipse(
        centerX,
        centerY,
        Math.max(0.5, bloomRadius * (profile.stretchX + 0.08)),
        Math.max(0.5, bloomRadius * (profile.stretchY + 0.08)),
        profile.rotation,
        0,
        Math.PI * 2,
      );
      ctx.closePath();
      ctx.fill();
    }
  }

  ctx.restore();
}

function drawJitterBrushSegment(ctx, startPoint, endPoint, options, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx || !startPoint) return dabIndexStart;

  const spacing = getJitterBrushSpacing(options.size, options.brushSettings);
  const strokeSeed = getStrokeSeed([startPoint, endPoint || startPoint], options);
  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);

  if (shouldIncludeStart) {
    drawWatercolorBlob(ctx, startPoint, options, strokeSeed, dabIndex);
    dabIndex += 1;
  }

  if (!endPoint) {
    return dabIndex;
  }

  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  const distance = Math.hypot(dx, dy);

  if (distance <= 0.0001) {
    if (!shouldIncludeStart) {
      drawWatercolorBlob(ctx, endPoint, options, strokeSeed, dabIndex);
      dabIndex += 1;
    }
    return dabIndex;
  }

  const steps = Math.max(1, Math.ceil(distance / spacing));
  for (let step = 1; step <= steps; step += 1) {
    const t = step / steps;
    drawWatercolorBlob(
      ctx,
      {
        x: startPoint.x + dx * t,
        y: startPoint.y + dy * t,
      },
      options,
      strokeSeed,
      dabIndex,
    );
    dabIndex += 1;
  }

  return dabIndex;
}

function stampInterpolatedBrush(ctx, points, options) {
  if (!ctx || !Array.isArray(points) || !points.length) return;
  let dabIndex = drawJitterBrushSegment(ctx, points[0], null, options, 0, true);
  for (let i = 1; i < points.length; i += 1) {
    dabIndex = drawJitterBrushSegment(ctx, points[i - 1], points[i], options, dabIndex, false);
  }
}

export function drawSingleBrushPoint(ctx, point, options) {
  if (!ctx || !point) return;

  if ((options && options.brushPreset) === 'jitter_blob') {
    drawWatercolorBlob(ctx, point, options, getStrokeSeed([point], options), 0);
    return;
  }

  const safeOpacity = clampOpacity(options.opacity);
  const fillStyle = options.isEraser ? 'rgba(0,0,0,1)' : options.rgba;
  const { radius, blurRadius, coreRadius, softness } = getBrushSoftMetrics(options.size, options.softness);

  ctx.save();
  ctx.globalCompositeOperation = options.isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = safeOpacity;
  ctx.fillStyle = fillStyle;
  ctx.strokeStyle = fillStyle;
  ctx.shadowBlur = 0;
  ctx.filter = blurRadius > 0.01 ? `blur(${blurRadius}px)` : 'none';

  ctx.beginPath();
  ctx.arc(point.x, point.y, softness > 0 ? coreRadius : radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export function getStrokeTravel(points) {
  if (!Array.isArray(points) || points.length <= 1) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    total += Math.hypot(dx, dy);
  }
  return total;
}

export function getStrokeSpread(points) {
  if (!Array.isArray(points) || points.length <= 1) return 0;
  const origin = points[0];
  let maxDistance = 0;
  for (let i = 1; i < points.length; i += 1) {
    const dx = points[i].x - origin.x;
    const dy = points[i].y - origin.y;
    maxDistance = Math.max(maxDistance, Math.hypot(dx, dy));
  }
  return maxDistance;
}


function interpolateBrushPoint(a, b, t) {
  const safeT = Math.max(0, Math.min(1, Number(t) || 0));
  const pressureA = Number(a && a.pressure);
  const pressureB = Number(b && b.pressure);
  const point = {
    x: (Number(a && a.x) || 0) + (((Number(b && b.x) || 0) - (Number(a && a.x) || 0)) * safeT),
    y: (Number(a && a.y) || 0) + (((Number(b && b.y) || 0) - (Number(a && a.y) || 0)) * safeT),
  };

  if (Number.isFinite(pressureA) || Number.isFinite(pressureB)) {
    const safePressureA = Number.isFinite(pressureA) ? pressureA : 0.68;
    const safePressureB = Number.isFinite(pressureB) ? pressureB : safePressureA;
    point.pressure = safePressureA + ((safePressureB - safePressureA) * safeT);
  }

  [
    'tiltX',
    'tiltY',
    'pointerTiltX',
    'pointerTiltY',
    'twist',
    'tangentialPressure',
    'altitudeAngle',
    'azimuthAngle',
  ].forEach((key) => {
    const valueA = Number(a && a[key]);
    const valueB = Number(b && b[key]);
    if (!Number.isFinite(valueA) && !Number.isFinite(valueB)) return;
    const safeValueA = Number.isFinite(valueA) ? valueA : valueB;
    const safeValueB = Number.isFinite(valueB) ? valueB : safeValueA;
    point[key] = safeValueA + ((safeValueB - safeValueA) * safeT);
  });

  if (typeof (a && a.pointerType) === 'string') point.pointerType = a.pointerType;
  else if (typeof (b && b.pointerType) === 'string') point.pointerType = b.pointerType;

  return point;
}

function midpointBrushPoint(a, b) {
  return interpolateBrushPoint(a, b, 0.5);
}

function quadraticBrushPoint(start, control, end, t) {
  const startToControl = interpolateBrushPoint(start, control, t);
  const controlToEnd = interpolateBrushPoint(control, end, t);
  return interpolateBrushPoint(startToControl, controlToEnd, t);
}

function buildQuadraticBrushPoints(start, control, end, options) {
  const approximateLength = Math.hypot(control.x - start.x, control.y - start.y) + Math.hypot(end.x - control.x, end.y - control.y);
  const safeSize = Math.max(1, Number(options && options.size) || 1);
  const sampleSpacing = Math.max(1.2, Math.min(7, safeSize * 0.42));
  const steps = Math.max(2, Math.min(96, Math.ceil(approximateLength / sampleSpacing)));
  const points = [];

  for (let i = 0; i <= steps; i += 1) {
    points.push(quadraticBrushPoint(start, control, end, i / steps));
  }

  return points;
}


export function drawGpuRoundBrushStrokeWithinSelection(ctx, points, options, selection) {
  if (!ctx || typeof ctx.drawRoundBrushStroke !== 'function') return false;
  if (!Array.isArray(points) || !points.length) return false;
  if (selection && selection.bounds) return false;
  ctx.drawRoundBrushStroke(points, options || {});
  return true;
}

export function drawGpuEllipseBrushStrokeWithinSelection(ctx, points, options, ellipseOptions, selection) {
  if (!ctx || typeof ctx.drawEllipseBrushStroke !== 'function') return false;
  if (!Array.isArray(points) || !points.length) return false;
  if (selection && selection.bounds) return false;
  return ctx.drawEllipseBrushStroke(points, options || {}, ellipseOptions || {});
}

export function drawGpuRoundBrushCurveSegmentWithinSelection(
  ctx,
  previousPreviousPoint,
  previousPoint,
  currentPoint,
  options,
  selection,
) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !currentPoint) return false;

  const curveStart = midpointBrushPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointBrushPoint(previousPoint, currentPoint);
  const points = buildQuadraticBrushPoints(curveStart, previousPoint, curveEnd, options);
  return drawGpuRoundBrushStrokeWithinSelection(ctx, points, options, selection);
}

export function drawGpuRoundBrushFinalTailWithinSelection(ctx, previousPoint, currentPoint, options, selection) {
  if (!ctx || !previousPoint || !currentPoint) return false;

  const tailStart = midpointBrushPoint(previousPoint, currentPoint);
  return drawGpuRoundBrushStrokeWithinSelection(ctx, [tailStart, currentPoint], options, selection);
}

export function drawBrushCurveSegmentWithinSelection(
  ctx,
  previousPreviousPoint,
  previousPoint,
  currentPoint,
  options,
  selection,
) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !currentPoint) return;

  const curveStart = midpointBrushPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointBrushPoint(previousPoint, currentPoint);
  const points = buildQuadraticBrushPoints(curveStart, previousPoint, curveEnd, options);
  drawBrushStrokeWithinSelection(ctx, points, options, selection);
}

export function drawBrushFinalTailWithinSelection(ctx, previousPoint, currentPoint, options, selection) {
  if (!ctx || !previousPoint || !currentPoint) return;

  const tailStart = midpointBrushPoint(previousPoint, currentPoint);
  drawBrushStrokeWithinSelection(ctx, [tailStart, currentPoint], options, selection);
}

export function drawBrushStroke(ctx, points, options) {
  if (!ctx || !Array.isArray(points) || points.length <= 0) return;


  const travel = getStrokeTravel(points);
  const spread = getStrokeSpread(points);
  const tinyStrokeThreshold = Math.max(0.5, Number(options.size || 1) * 0.08);

  if (points.length === 1 || (travel <= tinyStrokeThreshold && spread <= tinyStrokeThreshold)) {
    drawSingleBrushPoint(ctx, points[0], options);
    return;
  }

  if ((options && options.brushPreset) === 'jitter_blob') {
    stampInterpolatedBrush(ctx, points, options);
    return;
  }

  const fillStyle = options.isEraser ? 'rgba(0,0,0,1)' : options.rgba;
  const { radius, blurRadius, coreRadius, softness } = getBrushSoftMetrics(options.size, options.softness);

  ctx.save();
  ctx.globalCompositeOperation = options.isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = clampOpacity(options.opacity);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = fillStyle;
  ctx.fillStyle = fillStyle;
  ctx.shadowBlur = 0;
  ctx.filter = blurRadius > 0.01 ? `blur(${blurRadius}px)` : 'none';
  ctx.lineWidth = softness > 0 ? Math.max(1, coreRadius * 2) : Math.max(1, radius * 2);
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  if (points.length === 2) {
    ctx.lineTo(points[1].x, points[1].y);
  } else {
    for (let i = 1; i < points.length - 1; i += 1) {
      const midpoint = midpointBrushPoint(points[i], points[i + 1]);
      ctx.quadraticCurveTo(points[i].x, points[i].y, midpoint.x, midpoint.y);
    }
    const finalPoint = points[points.length - 1];
    ctx.lineTo(finalPoint.x, finalPoint.y);
  }

  ctx.stroke();
  ctx.restore();
}

export function drawBrushSegmentWithinSelection(ctx, startPoint, endPoint, options, selection, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx) return dabIndexStart;
  if ((options && options.brushPreset) !== 'jitter_blob') {
    return dabIndexStart;
  }

  if (!selection || !selection.bounds) {
    return drawJitterBrushSegment(ctx, startPoint, endPoint, options, dabIndexStart, shouldIncludeStart);
  }

  const clipped = clipContextToSelection(ctx, selection);
  const nextDabIndex = drawJitterBrushSegment(ctx, startPoint, endPoint, options, dabIndexStart, shouldIncludeStart);
  if (clipped) {
    ctx.restore();
  }
  return nextDabIndex;
}

export function drawBrushStrokeWithinSelection(ctx, points, options, selection) {
  if (!ctx) return;
  if (!selection || !selection.bounds) {
    drawBrushStroke(ctx, points, options);
    return;
  }

  const clipped = clipContextToSelection(ctx, selection);
  drawBrushStroke(ctx, points, options);
  if (clipped) {
    ctx.restore();
  }
}

export function getSoftBrushMaskAlpha(distance, radius, softness) {
  if (radius <= 0) return 0;
  if (distance >= radius) return 0;
  const safeSoftness = clampSoftness(softness);
  if (safeSoftness <= 0) return 1;
  const innerRadius = radius * (1 - (safeSoftness / 100));
  if (distance <= innerRadius) return 1;
  const feather = Math.max(0.0001, radius - innerRadius);
  const t = Math.min(1, Math.max(0, (distance - innerRadius) / feather));
  return 1 - t;
}